import React, { useEffect, useRef } from 'react';
import { isCwpUserSignedIn } from '../../../../../../_helpers/isCwpUserSignedIn';

type TokenIframeProps = {
	iframeSrc: string; // URL to load in the iframe
};

const ChatblkTokenIframe: React.FC<TokenIframeProps> = ({ iframeSrc }) => {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);

	useEffect(() => {
		const manageIframe = () => {
			const isSignedIn = isCwpUserSignedIn();
			const token = sessionStorage.getItem('chatblk_access_token');
			const iframeLoaded = sessionStorage.getItem('chatblk_iframe_loaded'); // Check if iframe has already been loaded

			// If user is not signed in but token exists, remove it
			if (!isSignedIn && token) {
				sessionStorage.removeItem('chatblk_access_token');
				sessionStorage.removeItem('chatblk_iframe_loaded'); // Reset iframe loaded flag
				return;
			}

			// If user is signed in and token does not exist, load the iframe for the first time
			if (isSignedIn && !token && iframeLoaded !== 'true') {
				if (iframeRef.current) {
					iframeRef.current.src = iframeSrc;
					sessionStorage.setItem('chatblk_iframe_loaded', 'true'); // Mark iframe as loaded
				}
				return;
			}

			// If user is signed in and token exists, check its validity
			if (isSignedIn && token) {
				try {
					const parsedToken = JSON.parse(token);
					const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
					const tokenCreatedAt = Math.floor(parsedToken.created_at / 1000); // Convert `created_at` to seconds
					const tokenExpiresIn = parsedToken.expires_in; // Token expiration time in seconds
					const tokenExpiryTime = tokenCreatedAt + tokenExpiresIn;

					// Calculate time until token expiry
					const timeUntilExpiry = tokenExpiryTime - currentTime;

					if (timeUntilExpiry <= 0) {
						// Token has already expired, reload the iframe
						if (iframeRef.current && iframeLoaded !== 'true') {
							iframeRef.current.src = iframeSrc;
							sessionStorage.setItem('chatblk_iframe_loaded', 'true'); // Mark iframe as loaded
						}
					} else if (timeUntilExpiry <= 300 && iframeLoaded !== 'true') {
						// If token is within 5 minutes of expiry and iframe hasn't been loaded, reload the iframe
						if (iframeRef.current) {
							iframeRef.current.src = iframeSrc;
							sessionStorage.setItem('chatblk_iframe_loaded', 'true'); // Mark iframe as loaded
							console.log('Token is close to expiry. Reloading iframe.');
						}
					} else {
						console.log(
							'Token is valid and not close to expiry. No action needed.'
						);
					}
				} catch (error) {
					console.error('Error parsing chatblk_access_token:', error);
				}
			}
		};

		// Reset iframeLoaded flag on component mount to ensure proper behavior
		sessionStorage.removeItem('chatblk_iframe_loaded');
		manageIframe();
	}, [iframeSrc]);

	// Function to handle iframe load and check the status of the loaded page
	const handleIframeLoad = () => {
		try {
			const iframeWindow = iframeRef.current?.contentWindow;

			if (iframeWindow) {
				// Check if the iframe's page returned a 401 status
				iframeWindow.postMessage({ type: 'CHECK_STATUS' }, '*');

				window.addEventListener('message', (event) => {
					if (event.data.type === 'STATUS_RESPONSE') {
						const { status } = event.data;
						if (status === 401) {
							sessionStorage.setItem('user_status', 'unverified_fp');
							console.log('User is an unverified FP user.');
						} else if (status === 200) {
							sessionStorage.setItem('user_status', 'verified_fp');
							console.log('User is a verified FP user.');
						}
					}
				});
			}
		} catch (error) {
			console.error('Error handling iframe load:', error);
		}
	};

	return (
		<iframe
			ref={iframeRef}
			src="" // Initially empty
			style={{ display: 'none' }}
			aria-hidden="true"
			tabIndex={-1}
			id="chatblk-iframe"
			onLoad={handleIframeLoad}
		/>
	);
};

export default ChatblkTokenIframe;
